import { type ReactNode, memo, useCallback, useMemo } from "react"
import Highlighter, { Chunk } from "react-highlight-words"

import { ensureArray } from "shared/lib/arrayUtils"

import "./styles/_text-highlighter.scss"

interface TextHighlighterProps {
  text: string
  ranges?: number[]
  searchTerm?: string | RegExp | (string | RegExp)[]
}

function rangesToChunks(text: string, ranges: number[] | undefined): Chunk[] {
  if (!ranges) return [{ start: 0, end: text.length }]

  const chunks: Chunk[] = []
  for (let i = 0; i < ranges.length; i += 2) {
    chunks.push({ start: ranges[i], end: ranges[i + 1] })
  }
  return chunks
}

function HighlightMark({ children }: { children: ReactNode }) {
  return (
    <mark aria-hidden="true" className="text-highlighter__mark">
      {children}
    </mark>
  )
}

export const TextHighlighter = memo(
  ({ text, ranges, searchTerm }: TextHighlighterProps) => {
    const findChunks = useCallback(
      () => rangesToChunks(text, ranges),
      [text, ranges]
    )

    const resolvedSearchTerm = useMemo(() => {
      return ensureArray(searchTerm)
    }, [searchTerm])

    if (!ranges && !searchTerm) return text

    return (
      <span aria-label={text}>
        <Highlighter
          aria-hidden="true"
          searchWords={resolvedSearchTerm}
          textToHighlight={text}
          findChunks={ranges ? findChunks : undefined}
          highlightTag={HighlightMark}
        />
      </span>
    )
  }
)
